import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useMarketingAssets, type MarketingAssetsData } from "../hooks/useMarketingAssets";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { EntityRecord } from "@/types/entity";

type TagFilter = "all" | number;
type VisibilityFilter = "all" | "public" | "restricted";
interface MarketingEditorContextValue extends MarketingAssetsData {
  entityRecord: EntityRecord;

  selectedAssetId: number | null;
  selectedAsset: MarketingAsset | null;
  selectAsset: (id: number) => void;

  temporalFilter: TagFilter;
  setTemporalFilter: (filter: TagFilter) => void;
  tagFilter: TagFilter;
  setTagFilter: (filter: TagFilter) => void;
  visibilityFilter: VisibilityFilter;
  setVisibilityFilter: (filter: VisibilityFilter) => void;

  tagsWithAssets: MarketingTag[];
  temporalTags: MarketingTag[];
  folderTags: MarketingTag[];
  contentTags: MarketingTag[];

  filteredAssets: MarketingAsset[];

  deleteAsset: (asset: MarketingAsset) => void;
  openFile: (asset: MarketingAsset) => void;

  toggleAssetTag: (tagId: number) => void;
  updateAssetField: <K extends keyof MarketingAsset>(field: K, value: MarketingAsset[K]) => void;

  getFileRecord: (asset: MarketingAsset) => any;
  getTemporalTag: (asset: MarketingAsset) => MarketingTag | undefined;
  getFolderTags: (asset: MarketingAsset) => MarketingTag[];
  getContentTags: (asset: MarketingAsset) => MarketingTag[];
  getProblemTags: (asset: MarketingAsset) => MarketingTag[];

  onCloseEditor: () => void;
}

const MarketingEditorContext = createContext<MarketingEditorContextValue | undefined>(undefined);

interface MarketingEditorProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord;
  onCloseEditor: () => void;
}

export const MarketingEditorProvider = ({ children, entityRecord, onCloseEditor }: MarketingEditorProviderProps) => {
  const { project, editorHandler } = useSwivuBryntumContext();
  const marketingData = useMarketingAssets(entityRecord);
  const { assets, allTags } = marketingData;

  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [temporalFilter, setTemporalFilter] = useState<TagFilter>("all");
  const [tagFilter, setTagFilter] = useState<TagFilter>("all");
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>("all");

  const tagsWithAssets = useMemo(() => {
    const tagIdsInUse = new Set(assets.flatMap((a) => a.tags ?? []));
    return allTags.filter((t) => tagIdsInUse.has(t.id));
  }, [assets, allTags]);

  const temporalTags = useMemo(
    () => allTags.filter((t) => t.type === MarketingTagType.TEMPORAL),
    [allTags],
  );

  const folderTags = useMemo(
    () => allTags.filter((t) => t.type === MarketingTagType.CONTENT || t.isFolderEligible),
    [allTags],
  );

  const contentTags = useMemo(
    () => allTags.filter((t) => t.type === MarketingTagType.SOLUTION),
    [allTags],
  );

  const filteredAssets = useMemo(() => {
    let result = assets;
    if (temporalFilter !== "all") {
      result = result.filter((a) => a.tags?.includes(temporalFilter as number));
    }
    if (tagFilter !== "all") {
      result = result.filter((a) => a.tags?.includes(tagFilter as number));
    }
    if (visibilityFilter === "public") {
      result = result.filter((a) => !a.isRestricted);
    } else if (visibilityFilter === "restricted") {
      result = result.filter((a) => a.isRestricted);
    }
    return result;
  }, [assets, temporalFilter, tagFilter, visibilityFilter]);

  const selectedAsset = useMemo(
    () =>
      selectedAssetId != null ? (filteredAssets.find((a) => a.id === selectedAssetId) ?? null) : null,
    [filteredAssets, selectedAssetId],
  );

  useEffect(() => {
    if (selectedAssetId == null) return;
    if (!filteredAssets.some((a) => a.id === selectedAssetId)) {
      setSelectedAssetId(null);
    }
  }, [filteredAssets, selectedAssetId]);

  const selectAsset = useCallback((id: number) => setSelectedAssetId(id), []);

  const deleteAsset = useCallback(
    (asset: MarketingAsset) => {
      project?.marketingAssetStore?.remove(asset.id);
      const remaining = assets.filter((a) => a.id !== asset.id);
      if (remaining.length === 0) {
        onCloseEditor();
        return;
      }
      if (selectedAssetId === asset.id) {
        setSelectedAssetId(null);
      }
    },
    [project, assets, selectedAssetId, onCloseEditor],
  );

  const openFile = useCallback(
    (_asset: MarketingAsset) => {
      editorHandler.openEditor(
        EDITOR_KEYS.taskFilesEditor,
        entityRecord,
        { task: entityRecord },
        { by: "recordType", useExisting: true },
      );
    },
    [editorHandler, entityRecord],
  );

  const toggleAssetTag = useCallback(
    (tagId: number) => {
      if (!selectedAsset) return;
      const current: number[] = selectedAsset.tags ?? [];
      selectedAsset.tags = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
    },
    [selectedAsset],
  );

  const updateAssetField = useCallback(
    <K extends keyof MarketingAsset>(field: K, value: MarketingAsset[K]) => {
      if (!selectedAsset) return;
      (selectedAsset as any)[field] = value;
    },
    [selectedAsset],
  );

  const getFileRecord = useCallback(
    (asset: MarketingAsset) => project?.boardFilesStore?.getById(asset.file) as any,
    [project],
  );

  const getTemporalTag = useCallback(
    (asset: MarketingAsset) =>
      temporalTags.find((t) => asset.tags?.includes(t.id)),
    [temporalTags],
  );

  const getFolderTags = useCallback(
    (asset: MarketingAsset) =>
      allTags.filter((t) => t.isFolderEligible && asset.tags?.includes(t.id)),
    [allTags],
  );

  const getContentTags = useCallback(
    (asset: MarketingAsset) =>
      allTags.filter((t) => t.type === MarketingTagType.SOLUTION && asset.tags?.includes(t.id)),
    [allTags],
  );

  const getProblemTags = useCallback(
    (asset: MarketingAsset) =>
      allTags.filter((t) => t.type === MarketingTagType.PROBLEM && asset.tags?.includes(t.id)),
    [allTags],
  );

  const value: MarketingEditorContextValue = {
    ...marketingData,
    entityRecord,
    selectedAssetId,
    selectedAsset,
    selectAsset,
    temporalFilter,
    setTemporalFilter,
    tagFilter,
    setTagFilter,
    visibilityFilter,
    setVisibilityFilter,
    tagsWithAssets,
    temporalTags,
    folderTags,
    contentTags,
    filteredAssets,
    deleteAsset,
    openFile,
    toggleAssetTag,
    updateAssetField,
    getFileRecord,
    getTemporalTag,
    getFolderTags,
    getContentTags,
    getProblemTags,
    onCloseEditor,
  };

  return (
    <MarketingEditorContext.Provider value={value}>
      {children}
    </MarketingEditorContext.Provider>
  );
};

export const useMarketingEditorContext = () => {
  const ctx = useContext(MarketingEditorContext);
  if (!ctx) throw new Error("useMarketingEditorContext must be used within MarketingEditorProvider");
  return ctx;
};
